import {Color} from '@benchling/nucleus-ui';
import React from 'react';
import {type Edge, type EdgeProps, MarkerType} from 'reactflow';

import BaseEdgeRenderer from 'ui-library/reactflow/edges/BaseEdgeRenderer';
import {EdgeType} from 'ui-library/reactflow/edges/edgeOptions';

import type {FlowchartEdgeSpec} from './ProcedureRunDesignFlowchartLayoutUtils';
import {getProcedureRunDesignFlowchartNodeHandleId} from './ProcedureRunDesignFlowchartNode';

export const PROCEDURE_RUN_DESIGN_FLOWCHART_EDGE_TYPE = 'procedureRunDesignFlowchartEdge';

/**
 * Visual variant for a design flowchart edge. Add a variant plus a color entry to introduce new tones.
 * `downstream` is the lighter tone for edges whose source is at or after the active unit-op layer.
 */
export type ProcedureRunDesignFlowchartEdgeVariant = 'default' | 'downstream';

export interface ProcedureRunDesignFlowchartEdgeData {
  variant: ProcedureRunDesignFlowchartEdgeVariant;
}

/** Maps an edge variant to its stroke color. */
const EDGE_VARIANT_COLORS: Record<ProcedureRunDesignFlowchartEdgeVariant, string> = {
  default: Color.GRAY_600,
  downstream: Color.GRAY_400,
};

/** Stroke/marker color for a variant. ReactFlow markers are per-edge, so arrowheads match the line. */
export function getProcedureRunDesignFlowchartEdgeColor(
  variant: ProcedureRunDesignFlowchartEdgeVariant
): string {
  return EDGE_VARIANT_COLORS[variant];
}

export function getProcedureRunDesignFlowchartEdgeId(sourceNodeId: string, targetNodeId: string): string {
  return `${sourceNodeId}->${targetNodeId}`;
}

export function makeProcedureRunDesignFlowchartEdge(
  spec: FlowchartEdgeSpec
): Edge<ProcedureRunDesignFlowchartEdgeData> {
  const variant = spec.variant ?? 'default';
  return {
    id: getProcedureRunDesignFlowchartEdgeId(spec.sourceNodeId, spec.targetNodeId),
    type: PROCEDURE_RUN_DESIGN_FLOWCHART_EDGE_TYPE,
    source: spec.sourceNodeId,
    target: spec.targetNodeId,
    sourceHandle: getProcedureRunDesignFlowchartNodeHandleId(spec.sourceNodeId, 'source'),
    targetHandle: getProcedureRunDesignFlowchartNodeHandleId(spec.targetNodeId, 'target'),
    data: {variant},
    markerEnd: {type: MarkerType.Arrow, color: getProcedureRunDesignFlowchartEdgeColor(variant)},
  };
}

/** Renders a design flowchart edge as a bezier curve colored by its variant. */
export default function ProcedureRunDesignFlowchartEdge(
  props: EdgeProps<ProcedureRunDesignFlowchartEdgeData>
): JSX.Element {
  const variant = props.data?.variant ?? 'default';
  return (
    <BaseEdgeRenderer
      {...props}
      data={{type: EdgeType.BEZIER, color: getProcedureRunDesignFlowchartEdgeColor(variant)}}
    />
  );
}

export const EDGE_TYPES = {
  [PROCEDURE_RUN_DESIGN_FLOWCHART_EDGE_TYPE]: ProcedureRunDesignFlowchartEdge,
};
